import { cva } from "class-variance-authority";
import { forwardRef, Fragment } from "react";
import { cn } from "../../../lib";

import type {
  GroupedTableSectionProps,
  GroupedTableActions,
  GroupedTableRowProps,
  GroupedTableCellProps,
} from "./types";
import { TintedLabel, AsyncCheckbox, Loader } from "../../atoms";
import { Button } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { formatSlug } from "@liberty-fund/utils/formatting";

const gridVariants = cva("grid mx-4", {
  variants: {
    variant: {
      base: "grid-cols-5",
      selection: "grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr]",
      expansion: "grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]",
      all: "grid-cols-[auto_1fr_1fr_1fr_1fr_1fr_1fr_auto]",
    },
  },
  defaultVariants: {
    variant: "base",
  },
});

interface GroupedTableProps extends React.HTMLAttributes<HTMLDivElement> {
  testId?: string;
  section: GroupedTableSectionProps;
}

const GroupedTable = forwardRef<HTMLDivElement, GroupedTableProps>(
  (props, _) => {
    const { className, section, testId } = props;
    const gridVariant = calculateGridVariant(section);
    return (
      <div
        key={`${section.id}_header`}
        className={cn(gridVariants({ variant: gridVariant }), className)}
      >
        {/* Header */}
        <div
          className={cn(
            "sticky self-start z-9 top-[130px] grid grid-cols-subgrid h-auto shadow-lg col-span-full",
          )}
        >
          {/* Optional Section Header */}
          {section.header && (
            <div className="col-span-full">
              <section.header className="overflow-hidden" />
            </div>
          )}
          {/* Column Headers */}
          {/* TODO - support select all CheckBox in column header */}
          {section.supportsSelection && (
            <div className="flex justify-center bg-lf-gray-light">
              <div>{""}</div>
            </div>
          )}
          {section.columnHeaders.map((item, index) => (
            <div
              key={`${item}_${index}`}
              className="flex justify-start bg-lf-gray-light"
            >
              <div className="p-2 text-black">{item}</div>
            </div>
          ))}
          {section.supportsExpansion && (
            <div className="flex justify-center bg-lf-gray-light">
              <div>{""}</div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-subgrid col-span-full">
          {section.rowsLoading && (
            <div
              className="col-span-full"
              data-testid={
                testId ? `${testId}-participants-loading` : undefined
              }
            >
              <Loader text="participants..." />
            </div>
          )}
          {section.rows.map((row, index) =>
            renderRow(section, row, index, testId),
          )}
        </div>
      </div>
    );
  },
);

const renderRow = (
  section: GroupedTableSectionProps,
  row: GroupedTableRowProps<any>,
  index: number,
  testId?: string,
) => {
  const odd = index % 2 == 1;

  return (
    <Fragment key={`${section.id}_${row.id}_${index}`}>
      {/* Row Header */}
      {/* Render all the cells of the row */}
      {row.cells.map((cell, index) => (
        <Fragment key={`${section.id}_${row.id}_cell_${index}`}>
          {renderCell(section, row, cell, index, odd, testId)}
        </Fragment>
      ))}

      {/* Render expandable content if it applies */}
      {(() => {
        if (row.expanded && row.expandedComponent) {
          const ExpandedRow = row.expandedComponent;
          return (
            <div
              key={`${section.id}_${row.id}_expanded`}
              className="col-span-full"
            >
              <ExpandedRow className="bg-lf-yellow" />
            </div>
          );
        }
      })()}
    </Fragment>
  );
};

const renderCell = (
  section: GroupedTableSectionProps,
  row: GroupedTableRowProps<any>,
  cell: GroupedTableCellProps,
  index: number,
  odd: boolean,
  testId?: string,
) => {
  const bgColor = odd ? "bg-white" : "bg-lf-gray";

  switch (cell.type) {
    case "list": {
      const tintColor =
        row.selected && index == 1 ? "bg-lf-yellow-light" : bgColor;
      if (cell.contentList && cell.contentList?.length > 0) {
        return (
          <div
            className={cn(
              tintColor,
              "p-2 flex-col justify-center items-center",
            )}
          >
            {cell.contentList.map((item: any, index: number) => (
              <p key={`${section.id}_${row.id}_${index}_item_${index}`}>
                {item}
              </p>
            ))}
          </div>
        );
      } else {
        return (
          <div className={cn(tintColor, "p-2 flex justify-start items-center")}>
            <TintedLabel variant="red" label="Missing" className="" />
          </div>
        );
      }
    }
    case "checkbox": {
      return (
        <>
          <AsyncCheckbox
            testId={testId || ""}
            size={"medium"}
            selectedTintColor={"yellow"}
            className={row.selected ? undefined : bgColor}
            initialState={row.selected}
            asyncCallback={(selected: boolean, completion: () => void) => {
              section.actions.toggleRowSelection?.(row, selected, completion);
            }}
          />
        </>
      );
    }
    case "details": {
      var buttonId = formatSlug(`${section.eNumber}-${cell.title}`);
      return (
        <Button
          id={`${buttonId}-info`}
          className={cn(
            bgColor,
            "px-2 flex justify-center hover:cursor-pointer scroll-mt-[50vh]",
          )}
          onClick={() => {
            section.actions.displayRowDetails?.(row);
          }}
        >
          <InformationCircleIcon
            className="mx-auto self-center h-6 w-6 text-gray-400"
            aria-hidden="true"
            data-testid={testId ? `${testId}-info-icon` : ""}
          />
          <span className="sr-only">, {cell.title}</span>
        </Button>
      );
    }
    case "expand": {
      return (
        <div
          className={cn(
            bgColor,
            "px-2 flex justify-center hover:cursor-pointer",
          )}
          onClick={() =>
            section.actions.toggleRowExpansion?.(row, !row.expanded)
          }
        >
          <p className="mx-auto self-center">{row.expanded ? "-" : "+"}</p>
        </div>
      );
    }
    case "custom": {
      const tintColor =
        row.selected && index == 1 ? "bg-lf-yellow-light" : bgColor;
      if (cell.component) {
        return (
          <div
            className={cn(
              tintColor,
              "p-2 flex-col justify-center items-center",
            )}
          >
            {cell.component({})}
          </div>
        );
      } else {
        return (
          <div className={cn(tintColor, "p-2 flex justify-start items-center")}>
            <TintedLabel variant="red" label="Missing" className="" />
          </div>
        );
      }
    }
    default: {
      // First data column is tinted to match the textbox selection
      const tintColor =
        row.selected && index == 1 ? "bg-lf-yellow-light" : bgColor;
      return (
        <div className={cn(tintColor, "p-2 flex justify-start items-center")}>
          {cell.title && (
            <p
              data-testid={
                testId && cell.testId ? `${testId}-${cell.testId}` : ""
              }
            >
              {cell.title}
            </p>
          )}
          {!cell.title && (
            <TintedLabel variant="red" label="Missing" className="" />
          )}
        </div>
      );
    }
  }
};

const calculateGridVariant = ({
  supportsSelection,
  supportsExpansion,
}: GroupedTableSectionProps) => {
  if (supportsExpansion && supportsSelection) {
    return "all";
  }

  if (supportsSelection) {
    return "selection";
  }

  if (supportsExpansion) {
    return "expansion";
  }

  return "base";
};

GroupedTable.displayName = "GroupedTable";

export default GroupedTable;
export type {
  GroupedTableProps,
  GroupedTableActions,
  GroupedTableSectionProps,
  GroupedTableRowProps,
  GroupedTableCellProps,
};
